import { useState, useCallback, useRef } from 'react'
import { OracleAgentChatInput } from '../../../../graphql-types'
import { useOnboardingChatSubscriptionSubscription } from '../queries.generated'

/** Client signal shape from backend
 * (e.g. { type: 'member_onboarding', payload: { success: true } })
 * */
export interface ClientSignalPayload {
  type: string
  payload?: unknown
}

export interface UseOnboardingChatSubscriptionParams {
  personalityId: string
  jwt: string
  chatSessionId?: string | null
  onMessageComplete?: (data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    userMessage: string
    oracleMessage: string
  }) => void
  onError?: (error: Error) => void
  onSessionReceived?: (sessionId: string) => void
  /** Called on final event when clientSignals are present
   * (e.g. member_onboarding success). Only this indicates onboarding complete.
   * */
  onClientSignals?: (signals: ClientSignalPayload[]) => void
  language?: string
}

export interface UseOnboardingChatSubscriptionReturn {
  streamingText: string
  isStreaming: boolean
  pendingMessage: string
  sendMessage: (message: string) => void
  error: Error | null
}

/**
 * Hook for member onboarding chat. Uses chatChannel: 'member_onboarding'.
 * Client signals (e.g. onboarding submit success) are only on the final subscription event.
 */
export function useOnboardingChatSubscription ({
  personalityId,
  jwt,
  chatSessionId = null,
  onMessageComplete,
  onError,
  onSessionReceived,
  onClientSignals,
  language,
}: UseOnboardingChatSubscriptionParams): UseOnboardingChatSubscriptionReturn {
  const [subscriptionInput, setSubscriptionInput] = useState<OracleAgentChatInput | null>(null)
  const [streamingText, setStreamingText] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [pendingMessage, setPendingMessage] = useState('')
  const [error, setError] = useState<Error | null>(null)
  const streamingTextRef = useRef('')

  useOnboardingChatSubscriptionSubscription({
    variables: { input: subscriptionInput! },
    skip: !subscriptionInput,
    context: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    onData: ({ data: subscriptionData }) => {
      const payload = subscriptionData.data?.oracleAgentChat
      if (!payload) return

      const rawPayload = payload as {
        clientSignals?: Array<{ type: string; payload?: unknown }>;
        client_signals?: Array<{ type: string; payload?: unknown }>;
        chatChannel?: string;
      }
      const signals = rawPayload.clientSignals || rawPayload.client_signals || []

      if (payload.textChunk) {
        streamingTextRef.current += payload.textChunk
        setStreamingText(streamingTextRef.current)
      }

      if (payload.chatSessionId && !chatSessionId) {
        onSessionReceived?.(payload.chatSessionId)
      }

      if (payload.errorInfo && payload.errorInfo.length > 0) {
        const errorMessage = payload.errorInfo[0].message
        const errorCode = payload.errorInfo[0].code
        const err = new Error(`${errorCode}: ${errorMessage}`)
        setError(err)
        onError?.(err)
      }

      if (payload.isFinal) {
        setIsStreaming(false)
        setSubscriptionInput(null)

        if (payload.success && payload.chatSessionId && payload.messagePairId) {
          onMessageComplete?.({
            messagePairId: payload.messagePairId,
            chatSessionId: payload.chatSessionId,
            sessionTitle: payload.sessionTitle ?? undefined,
            userMessage: pendingMessage,
            oracleMessage: streamingTextRef.current,
          })
        }

        if (signals.length > 0) {
          onClientSignals?.(signals.map((s) => ({ type: s.type, payload: s.payload })))
        }
      }
    },
    onError: (subscriptionError) => {
      setError(subscriptionError)
      setIsStreaming(false)
      setSubscriptionInput(null)
      onError?.(subscriptionError)
    },
  })

  const sendMessage = useCallback((message: string) => {
    streamingTextRef.current = ''
    setStreamingText('')
    setPendingMessage(message)
    setIsStreaming(true)
    setError(null)

    const input: OracleAgentChatInput = {
      personalityId,
      message,
      chatSessionId,
      chatChannel: 'member_onboarding',
      contentId: null,
      featureMediaId: null,
      mode: 'text',
      language,
    }

    setSubscriptionInput(input)
  }, [personalityId, chatSessionId, language])

  return {
    streamingText,
    isStreaming,
    pendingMessage,
    sendMessage,
    error,
  }
}
